/* ============================================================
   Pure Cartel — Membership Portal
   ------------------------------------------------------------
   Presentation layer only. It reads ONE record (window.PureCartel.member,
   rendered by sections/membership.liquid) and paints every view from it.

   ── CONNECTING THE BACKEND ─────────────────────────────────────────────
   Replace the body of Data.load() below with a fetch that resolves the same
   shape, e.g.

       load() {
         return fetch(PC.membershipApiUrl + "/me", { credentials: "include" })
           .then((r) => r.json());
       }

   and set `source: "live"` on the response. Nothing else in this file, in
   membership.liquid, or in membership.css needs to change.

   This file deliberately does NOT touch the reservation/Stripe flow in
   product.js — "Reserve your membership" and "Update Payment Method" both
   hand off to that existing onboarding.
   ============================================================ */
(function () {
  "use strict";

  const root = document.getElementById("membership");
  if (!root) return;

  const PC = (window.PureCartel = window.PureCartel || {});

  /* ============================================================
     DATA + AUTH — live, session-backed
     ------------------------------------------------------------
     Both were placeholders until Sprint 3: Data.load() resolved a record
     inlined by Liquid, and Auth.signIn accepted any email/password pair
     without sending anything anywhere.

     They now talk to the Pure Cartel API. The credentials: "include" on every
     call is what carries the pc_member_session cookie — the storefront and
     api.purecartel.com share a registrable domain, so the browser treats
     these as same-site and sends it.
     ============================================================ */
  const API = String(PC.membershipApiUrl || "").replace(/\/$/, "");

  async function api(path, options) {
    const res = await fetch(API + path, Object.assign({
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    }, options || {}));

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const e = new Error(body.message || "request_failed");
      e.code = res.status;
      e.body = body;
      throw e;
    }
    return body;
  }

  const Data = {
    /*
     * Resolves the member record the whole portal paints from.
     *
     * Returns { signedIn: false } rather than throwing on 401, because an
     * expired or absent session is the ordinary case for a visitor, not an
     * error. routeFromHash() turns that into the sign-in view.
     *
     * There is deliberately NO placeholder fallback. If the API is
     * unreachable the portal shows the sign-in screen rather than a
     * fabricated membership.
     */
    load() {
      if (!API) {
        /* No API configured — theme-editor preview only. PC.member is
           rendered by Liquid in that case and is marked source:"placeholder". */
        return Promise.resolve(Object.assign({}, PC.member || { signedIn: false }));
      }
      return api("/member/me")
        .then((m) => Object.assign({}, m, { source: "live", signedIn: true }))
        .catch((e) => {
          if (e.code === 401) return { signedIn: false, source: "live" };
          console.error("[membership] could not load member:", e.message);
          return { signedIn: false, source: "live", loadError: true };
        });
    }
  };

  /*
   * Passwordless sign-in: email -> six-digit code -> session.
   *
   * requestCode never reports whether an email belongs to a member. The
   * backend returns an identical body either way, and this layer does not
   * try to infer anything from it — the generic message is shown verbatim.
   */
  const Auth = {
    requestCode(email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "")) {
        return Promise.reject(new Error("Enter a valid email address."));
      }
      return api("/member/auth/request-code", {
        method: "POST",
        body: JSON.stringify({ email: email })
      });
    },

    resend(challengeId) {
      return api("/member/auth/resend-code", {
        method: "POST",
        body: JSON.stringify({ challengeId: challengeId })
      });
    },

    verifyCode(challengeId, code) {
      if (!/^[0-9]{6}$/.test(String(code || "").trim())) {
        return Promise.reject(new Error("Enter the six-digit code."));
      }
      return api("/member/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ challengeId: challengeId, code: String(code).trim() })
      });
    },

    signOut() {
      /* Clear the onboarding continuity flags too. They are not credentials —
         they only decide whether the just-reserved customer sees their
         membership inline — but leaving them set after an explicit sign-out
         would be surprising. */
      try {
        window.sessionStorage.removeItem("pc_onboarding_complete");
        window.sessionStorage.removeItem("pc_onboarding_email");
      } catch (e) { /* nothing to clear */ }
      if (!API) return Promise.resolve();
      return api("/member/signout", { method: "POST" }).catch(() => ({}));
    }
  };

  let state = Object.assign({}, PC.member || {});

  /* ============================================================
     FORMATTING
     ============================================================ */
  const fmt = {
    memberNo(n) {
      return "#" + String(n == null ? 0 : n).padStart(7, "0");
    },
    statusLabel(s) {
      /* The five statuses the members table allows. Anything unrecognised
         falls back to the safest reading — pending — rather than showing a
         raw database value to a customer. */
      return {
        active:    "Active",
        pending:   "Reserved",
        suspended: "Suspended",
        cancelled: "Cancelled",
        rejected:  "Not Accepted"
      }[s] || "Reserved";
    },
    card(pm) {
      if (!pm || !pm.last4) return null;
      return {
        brand: (pm.brand || "Card").toUpperCase(),
        mask: "•••• •••• •••• " + pm.last4,
        exp:
          pm.expMonth && pm.expYear
            ? "Expires " + String(pm.expMonth).padStart(2, "0") + "/" + String(pm.expYear).slice(-2)
            : ""
      };
    }
  };

  /* ============================================================
     BINDING — data-bind (text) and data-bind-value (inputs)
     ============================================================ */
  function derive(s) {
    const card = fmt.card(s.paymentMethod);
    return {
      release: s.release || "—",
      tier: s.tier || "—",
      memberNumber: fmt.memberNo(s.memberNumber),
      reservedOn: s.reservedOn || "—",
      statusLabel: fmt.statusLabel(s.status),
      statusPill: fmt.statusLabel(s.status),
      monthlyPrice: s.monthlyPrice || "—",
      /* Member credit. Preformatted by the API (toPortalShape) so the dollar
         string is produced in one place; the raw cents ride along on state for
         whatever redemption is built later. Falls back to $0.00 rather than
         an em-dash — this is a balance, and a balance is always a number. */
      memberCredit: s.memberCredit || "$0.00",
      nextShipment: s.nextShipment || "—",
      nextBilling: s.nextBilling || "—",
      email: s.email || "—",
      pmBrand: card ? card.brand : "CARD",
      pmMask: card ? card.mask : "No card on file",
      pmExp: card ? card.exp : "",
      pmSummary: card ? card.brand + " ending " + s.paymentMethod.last4 : "No card on file",
      /* Shipping is the one thing the dashboard actively asks the member to
         do, so the nav card states its STATUS rather than a fragment of the
         address. `shipping` comes from our own shipping_profiles row via
         /member/me — null until they complete it. */
      addressCity: s.shipping
        ? s.shipping.city + ", " + (s.shipping.region || "")
        : "Not added yet",
      ordersCount: (s.orders && s.orders.length) || 0 ? s.orders.length + " order(s)" : "No orders yet",
      notifCount:
        (s.notifications && s.notifications.length) || 0
          ? s.notifications.length + " update(s)"
          : "No activity yet"
    };
  }

  function paint() {
    const view = derive(state);

    root.querySelectorAll("[data-bind]").forEach((el) => {
      const key = el.getAttribute("data-bind");
      if (!(key in view)) return;
      // the credential number animates; everything else sets directly
      if (el.hasAttribute("data-countup")) return;
      el.textContent = view[key];
    });

    root.querySelectorAll("[data-bind-value]").forEach((el) => {
      const path = el.getAttribute("data-bind-value").split(".");
      let v = state;
      for (const k of path) v = v == null ? undefined : v[k];
      el.value = v == null ? "" : v;
    });

    // status pill tone
    const pill = root.querySelector('[data-bind="statusPill"]');
    if (pill) {
      pill.classList.toggle("mbr__pill--active", state.status === "active");
      pill.classList.toggle("mbr__pill--ended",
        state.status === "cancelled" || state.status === "rejected");
    }

    /* State-scoped blocks. data-when takes a space-separated list, so one
       block can serve several statuses — the billing grid is shown to active
       AND suspended members, both of whom have a subscription to look at. */
    root.querySelectorAll("[data-when]").forEach((el) => {
      const states = (el.getAttribute("data-when") || "").split(/\s+/).filter(Boolean);
      el.hidden = states.indexOf(state.status) === -1;
    });

    renderJourney();
    renderFeed();
    renderOrders();
  }

  /* ---------- journey ---------- */
  function renderJourney() {
    const list = document.getElementById("mbrJourney");
    if (!list) return;
    const steps = state.journey || [];
    list.innerHTML = "";
    steps.forEach((step) => {
      const li = document.createElement("li");
      li.className = "mbr__step";
      if (step.state === "done") li.classList.add("is-done");
      if (step.state === "current") li.classList.add("is-current");

      const name = document.createElement("span");
      name.textContent = step.label;
      li.appendChild(name);

      if (step.when) {
        const when = document.createElement("span");
        when.className = "mbr__step-when";
        when.textContent = step.when;
        li.appendChild(when);
      }
      list.appendChild(li);
    });
  }

  /* ---------- notifications ---------- */
  function renderFeed() {
    const feed = document.getElementById("mbrFeed");
    if (!feed) return;
    const events = state.notifications || [];
    feed.innerHTML = "";

    if (!events.length) {
      const li = document.createElement("li");
      li.className = "mbr__empty-copy";
      li.textContent = "No activity yet.";
      feed.appendChild(li);
      return;
    }

    events.forEach((ev) => {
      const li = document.createElement("li");
      li.className = "mbr__event";
      const name = document.createElement("span");
      name.className = "mbr__event-name";
      name.textContent = ev.label;
      const when = document.createElement("span");
      when.className = "mbr__event-when";
      when.textContent = ev.when || "";
      li.appendChild(name);
      li.appendChild(when);
      feed.appendChild(li);
    });
  }

  /* ---------- orders ---------- */
  function renderOrders() {
    const wrap = document.getElementById("mbrOrders");
    if (!wrap) return;
    const orders = state.orders || [];
    wrap.innerHTML = "";

    if (!orders.length) {
      const box = document.createElement("div");
      box.className = "mbr__empty";
      const t = document.createElement("p");
      t.className = "mbr__empty-title";
      t.textContent = "No orders yet.";
      const c = document.createElement("p");
      c.className = "mbr__empty-copy";
      c.textContent = "Your first shipment will appear here once your membership becomes active.";
      box.appendChild(t);
      box.appendChild(c);
      wrap.appendChild(box);
      return;
    }

    orders.forEach((o) => {
      const box = document.createElement("div");
      box.className = "mbr__block";
      const dl = document.createElement("div");
      dl.className = "mbr__dl";
      [
        ["Order", o.name || "—"],
        ["Placed", o.placedOn || "—"],
        ["Status", o.status || "—"],
        ["Total", o.total || "—"]
      ].forEach(([k, v]) => {
        const row = document.createElement("div");
        row.className = "mbr__dl-row";
        const key = document.createElement("span");
        key.textContent = k;
        const val = document.createElement("span");
        val.className = "mbr__dl-val";
        val.textContent = v;
        row.appendChild(key);
        row.appendChild(val);
        dl.appendChild(row);
      });
      box.appendChild(dl);
      wrap.appendChild(box);
    });
  }

  /* ============================================================
     NUMBER ANIMATION — the credential counts up on first reveal
     ============================================================ */
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function countUp(el, target) {
    const final = fmt.memberNo(target);

    /* requestAnimationFrame is frozen while a tab is backgrounded. Without a
       guard the animation never starts and the customer is left looking at the
       markup's placeholder — a wrong member number, not merely a missing
       animation. Commit the value directly whenever we can't animate. */
    if (reduceMotion || !target || document.hidden) {
      el.textContent = final;
      return;
    }

    const duration = 900;
    const start = performance.now();
    // Belt and braces: if frames stop mid-count (tab hidden part-way), make
    // sure the correct number is on screen regardless.
    const safety = window.setTimeout(() => { el.textContent = final; }, duration + 400);

    function tick(now) {
      const p = Math.min(1, (now - start) / duration);
      // easeOutExpo — matches the cubic-bezier(0.16,1,0.3,1) feel of the sheet
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      el.textContent = fmt.memberNo(Math.round(target * eased));
      if (p < 1) {
        requestAnimationFrame(tick);
      } else {
        el.textContent = final;
        window.clearTimeout(safety);
      }
    }
    requestAnimationFrame(tick);
  }

  /* ============================================================
     VIEW ROUTING — hash-based on a page, so views are deep-linkable and
     survive a refresh. When embedded in the onboarding sheet the URL is left
     alone: the product page owns its own hashes (#purchaseOptions, #reviews,
     #faq) and the portal must not clobber them.
     ============================================================ */
  const isInline = root.hasAttribute("data-inline");
  const views = Array.from(root.querySelectorAll(".mbr__view"));
  const routes = {
    membership: "membership",
    payment: "panel-payment",
    address: "panel-address",
    orders: "panel-orders",
    notifications: "panel-notifications",
    settings: "panel-settings",
    plan: "panel-membership"
  };

  function show(viewName, opts) {
    const target = views.find((v) => v.getAttribute("data-view") === viewName);
    if (!target) return;

    views.forEach((v) => v.classList.remove("is-active"));
    target.classList.add("is-active");

    // restart the staggered rise on the portal home
    if (viewName === "membership") {
      target.querySelectorAll(".mbr__rise").forEach((el) => {
        el.style.animation = "none";
        void el.offsetWidth; // reflow so the animation replays
        el.style.animation = "";
      });
      const no = target.querySelector("[data-countup]");
      if (no) countUp(no, state.memberNumber);
    }

    if (isInline) {
      // scroll the onboarding sheet, never the page behind it
      const sheet = root.closest(".onb__sheet");
      if (sheet) sheet.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
      return;
    }

    if (!opts || !opts.silent) {
      const hash = Object.keys(routes).find((k) => routes[k] === viewName);
      if (hash) history.replaceState(null, "", "#" + hash);
    }
    window.scrollTo({ top: root.offsetTop - 80, behavior: reduceMotion ? "auto" : "smooth" });
  }

  function routeFromHash() {
    // Embedded: always the membership view — onboarding has just completed.
    if (isInline) return show("membership", { silent: true });
    if (!state.signedIn) return show("signin", { silent: true });
    const key = (location.hash || "").replace("#", "");
    show(routes[key] || "membership", { silent: true });
  }

  /* ============================================================
     WIRING
     ============================================================ */
  // sign in
  /* ============================================================
     SIGN IN — email, then the six-digit code
     ------------------------------------------------------------
     One form, two steps. The submit button and error line are shared, so the
     screen keeps its existing markup and rhythm rather than becoming two
     separate forms with two layouts.
     ============================================================ */
  const signInForm = document.getElementById("mbrSignIn");
  const signInErr  = document.getElementById("mbrSignInError");
  const signInNote = document.getElementById("mbrSignInNote");
  const signInBtn  = document.getElementById("mbrSignInBtn");

  if (signInForm) {
    let challengeId = null;
    let step = "email";

    const stepEls = (name) => signInForm.querySelectorAll('[data-step="' + name + '"]');
    const showErr = (msg) => { signInErr.textContent = msg; signInErr.hidden = false; };
    const showNote = (msg) => { signInNote.textContent = msg; signInNote.hidden = !msg; };

    function toStep(next) {
      step = next;
      stepEls("email").forEach((el) => { el.hidden = next !== "email"; });
      stepEls("code").forEach((el) => { el.hidden = next !== "code"; });
      signInBtn.textContent = next === "email" ? "Continue" : "Verify";
      if (next === "code") {
        const c = document.getElementById("mbrCode");
        if (c) { c.value = ""; window.setTimeout(() => c.focus(), 60); }
      }
    }

    function restart() {
      challengeId = null;
      signInErr.hidden = true;
      showNote("");
      toStep("email");
    }

    signInForm.addEventListener("submit", (e) => {
      e.preventDefault();
      signInErr.hidden = true;
      signInBtn.disabled = true;

      if (step === "email") {
        const email = document.getElementById("mbrEmail").value.trim();
        Auth.requestCode(email)
          .then((out) => {
            /* The message is the server's, shown verbatim. It is identical
               whether or not this email has a membership — the client must
               not paraphrase it into something that reveals which. */
            challengeId = out.challengeId;
            showNote(out.message);
            toStep("code");
          })
          .catch((err) => showErr(err.message || "Something went wrong. Please try again."))
          .finally(() => { signInBtn.disabled = false; });
        return;
      }

      const code = document.getElementById("mbrCode").value;
      Auth.verifyCode(challengeId, code)
        .then(() => Data.load())
        .then((member) => {
          if (!member.signedIn) throw new Error("We could not open your membership. Please try again.");
          state = member;
          paint();
          showNote("");
          show("membership");
        })
        .catch((err) => {
          const b = err.body || {};
          if (b.error === "challenge_expired" || b.error === "too_many_attempts") {
            showErr(err.message);
            restart();
          } else if (typeof b.attemptsRemaining === "number") {
            showErr(err.message + " " + b.attemptsRemaining + " attempt" +
                    (b.attemptsRemaining === 1 ? "" : "s") + " remaining.");
          } else {
            showErr(err.message || "That code could not be verified.");
          }
        })
        .finally(() => { signInBtn.disabled = false; });
    });

    const resendBtn = document.getElementById("mbrResend");
    if (resendBtn) {
      resendBtn.addEventListener("click", () => {
        if (!challengeId) return;
        resendBtn.disabled = true;
        signInErr.hidden = true;
        Auth.resend(challengeId)
          .then((out) => showNote(out.message || "A new code is on its way."))
          .catch((err) => showErr(err.message || "Could not resend the code."))
          .finally(() => { window.setTimeout(() => { resendBtn.disabled = false; }, 30000); });
      });
    }

    const restartBtn = document.getElementById("mbrRestart");
    if (restartBtn) restartBtn.addEventListener("click", restart);
  }

  // nav cards + back buttons
  root.querySelectorAll("[data-goto]").forEach((btn) => {
    btn.addEventListener("click", () => show(btn.getAttribute("data-goto")));
  });
  root.querySelectorAll("[data-back]").forEach((btn) => {
    btn.addEventListener("click", () => show("membership"));
  });

  // notification toggles
  root.querySelectorAll(".mbr__switch").forEach((sw) => {
    sw.addEventListener("click", () => {
      sw.setAttribute("aria-checked", sw.getAttribute("aria-checked") === "true" ? "false" : "true");
    });
  });

  // update payment method → hands off to the EXISTING onboarding, which owns
  // the Stripe SetupIntent flow. No card handling happens in this file.
  const updateCard = document.getElementById("mbrUpdateCard");
  if (updateCard) {
    updateCard.addEventListener("click", () => {
      if (PC.membershipReserveUrl) window.location.href = PC.membershipReserveUrl;
    });
  }

  /* ============================================================
     SHIPPING ADDRESS
     ------------------------------------------------------------
     The reservation deliberately collects no shipping address — it asks for a
     payment method and its billing details only, so Apple Pay can complete in
     one tap. This form is where the fulfilment address actually arrives, and
     it writes to shipping_profiles, which is the single source of truth the
     admin and Shopify fulfilment both read.

     Apartment / unit is optional on purpose: a house does not have one, and
     forcing "N/A" into address line 2 produces a worse label than an empty
     field. Every other line is required, because a parcel cannot be addressed
     without them.
     ============================================================ */
  const addressForm = document.getElementById("mbrAddress");
  if (addressForm) {
    const note = document.getElementById("mbrAddressNote");
    const submitBtn = addressForm.querySelector('button[type="submit"]');

    /* Paints the completion banner and the nav card together, so the two can
       never disagree about whether an address is on file. */
    function paintShipping(profile) {
      state.shipping = profile || null;
      const banner = document.getElementById("mbrShipState");
      if (banner) {
        banner.textContent = profile
          ? "Shipping information complete"
          : "Shipping address required";
        banner.classList.toggle("is-complete", Boolean(profile));
      }
      const meta = document.querySelector('[data-bind="addressCity"]');
      if (meta) {
        meta.textContent = profile
          ? profile.city + ", " + (profile.region || "")
          : "Not added yet";
      }
      if (submitBtn) {
        submitBtn.textContent = profile ? "Update Address" : "Save Address";
      }
    }
    paintShipping(state.shipping || null);

    addressForm.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!note) return;

      const payload = {
        firstName: (document.getElementById("mbrFirstName") || {}).value ||
                   state.firstName || "",
        lastName: (document.getElementById("mbrLastName") || {}).value ||
                  state.lastName || "",
        line1: (document.getElementById("mbrAddr1") || {}).value || "",
        line2: (document.getElementById("mbrAddr2") || {}).value || "",
        city: (document.getElementById("mbrCity") || {}).value || "",
        region: (document.getElementById("mbrRegion") || {}).value || "",
        postalCode: (document.getElementById("mbrZip") || {}).value || "",
        country: (document.getElementById("mbrCountry") || {}).value || "United States"
      };

      note.hidden = true;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = "Saving\u2026"; }

      api("/member/address", { method: "PUT", body: JSON.stringify(payload) })
        .then((res) => {
          paintShipping(res.shipping);
          note.textContent = "Shipping address saved.";
          note.hidden = false;
        })
        .catch((err) => {
          /* The API returns per-field messages; show the first so the member
             is told which line to fix rather than "something went wrong". */
          const d = err.body && err.body.details;
          note.textContent = (d && d.length && d[0].message) ||
            (err.body && err.body.message) ||
            "We could not save your address. Please try again.";
          note.hidden = false;
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = state.shipping ? "Update Address" : "Save Address";
          }
        });
    });
  }

  /* The reservation and reminder emails link to <dashboard>#shipping. Landing
     the member ON the form is the whole point of the link — asking them to
     find it themselves is how an address never gets added. */
  if (window.location.hash === "#shipping") {
    const go = document.querySelector('[data-goto="panel-address"]');
    if (go) window.setTimeout(() => go.click(), 0);
  }

  const signOut = document.getElementById("mbrSignOut");
  if (signOut) {
    signOut.addEventListener("click", () => {
      Auth.signOut().then(() => {
        state = Object.assign({}, PC.member || {}, { signedIn: false });
        history.replaceState(null, "", location.pathname);
        show("signin", { silent: true });
      });
    });
  }

  if (!isInline) window.addEventListener("hashchange", routeFromHash);

  /* ============================================================
     SESSION EXPIRY
     ------------------------------------------------------------
     The session is checked once at boot, which is not enough: a member can
     leave the dashboard open for days, and a 30-day session or an explicit
     sign-out elsewhere would leave this tab showing a membership the server
     no longer authorises.

     Re-checking when the tab regains focus is the cheap, correct moment — it
     costs nothing while the tab is hidden, and any expiry is caught before
     the member interacts again. A drop to signed-out returns them to the
     sign-in screen instead of leaving stale data on screen.

     Inline (post-onboarding) is exempt: there is no session there by design,
     and revalidating would eject the customer who just reserved.
     ============================================================ */
  if (!isInline && API) {
    let revalidating = false;
    const revalidate = () => {
      if (revalidating || document.visibilityState !== "visible") return;
      revalidating = true;
      Data.load()
        .then((member) => {
          const wasIn = state.signedIn;
          state = Object.assign({}, member);
          paint();
          /* Only re-route on a transition, so a member reading a panel is not
             yanked back to the dashboard home on every tab focus. */
          if (wasIn && !state.signedIn) show("signin", { silent: true });
        })
        .finally(() => { revalidating = false; });
    };
    document.addEventListener("visibilitychange", revalidate);
  }

  /* ---------- boot ---------- */
  Data.load().then((member) => {
    state = Object.assign({}, member);
    paint();
    routeFromHash();
  });

  // exposed for the onboarding handoff and the backend integration later
  PC.membership = {
    show: show,
    paint: paint,
    /* Called by the onboarding when the embedded portal is revealed. The
       entrance animations and the member-number count-up would otherwise have
       already played while the portal was still hidden. */
    enter: function () {
      paint();
      show("membership", { silent: true });
    },

    /* Hands the real reservation straight in.
       The API returns the customer's actual founding number, status and date.
       Without this the portal opens on the Liquid-rendered placeholder and
       shows a fabricated number to the person who just joined. */
    setMember: function (patch) {
      if (!patch) return;
      state = Object.assign({}, state, patch, { source: "live", signedIn: true });
      if (patch.status === "pending" || patch.status === "active") {
        state.journey = state.journey || [];
      }
      paint();
    },

    get state() { return state; }
  };
})();
